import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import GroupHero from "@/components/GroupHero";
import { EYES_BASE, COMMERCE_BASE } from "@/lib/routes";
import LegalLinks from "@/components/LegalLinks";

// Panel colours rise on real mouse movement hover or active press only (see index.css .hover-panel / hp-*)
const BUTTONS = [
  {
    key: "eyes",
    testid: "inc-btn-eyes-group",
    href: EYES_BASE || "/eagle-eyes",
    panel: "bg-[#f4f1eb]",
    hp: "hp-d",
    hpSub: "hp-d60",
  },
  {
    key: "commerce",
    testid: "inc-btn-commerce-group",
    href: COMMERCE_BASE,
    panel: "bg-brass",
    hp: "hp-d",
    hpSub: "hp-d60",
  },
];

// Card geometry is identical on every gateway page (same as Eagle Eyes / Eagle Commerce Group)
const CARD_INNER =
  "relative z-10 flex lg:min-h-[26vh] lg:[@media(max-height:920px)]:min-h-[21vh] flex-col justify-end px-6 py-7 md:px-10 lg:px-16 lg:py-8 lg:[@media(max-height:920px)]:py-5";

export default function IncGateway() {
  const { t } = useLang();
  const g = t.inc;

  return (
    <main className="grain bg-[#0a0a0a] text-[#f4f1eb] min-h-screen lg:h-screen flex flex-col lg:overflow-hidden relative" data-testid="inc-gateway">
      <header className="fixed top-0 inset-x-0 z-40 md:hidden">
        <div className="pl-6 pr-4 py-4 flex items-center justify-end">
          <LanguageSwitcher variant="dark" />
        </div>
      </header>
      <div className="hidden md:block fixed top-4 right-4 z-50">
        <LanguageSwitcher variant="dark" />
      </div>

      <GroupHero
        l1={g.hero1}
        l3={g.hero2}
        intro={g.intro}
        introUnderBlock
        introBelowMedallion
        testid="inc"
      />

      <section className="grid grid-cols-1 md:grid-cols-2 shrink-0" data-testid="inc-groups">
        {BUTTONS.map((b, i) => (
          <motion.div
            key={b.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
            className="contents"
          >
            <Link
              to={b.href}
              data-testid={b.testid}
              className="group relative block overflow-hidden"
            >
              <div className={`hover-panel absolute inset-x-0 bottom-0 top-0 rounded-t-[2.2rem] ${b.panel}`} />
              <div className={CARD_INNER}>
                <div>
                  <h2 className={`font-gateway font-bold text-2xl lg:text-[1.9rem] lg:[@media(max-height:920px)]:text-[1.35rem] tracking-tight mb-2 transition-colors duration-500 ${b.hp}`}>
                    {b.key === "eyes" ? g.eyesBtn : g.commerceBtn}
                  </h2>
                  <p className={`font-mono-data text-[0.58rem] lg:[@media(max-height:920px)]:text-[0.52rem] tracking-[0.2em] text-white/40 mb-3 lg:[@media(max-height:920px)]:mb-1.5 transition-colors duration-500 ${b.hpSub}`}>
                    {b.key === "eyes" ? g.eyesSub : g.commerceSub}
                  </p>
                  {/* Invisible spacer keeps these 2 cards exactly as tall as the 3-line-desc cards on Eyes/Commerce (desktop only) */}
                  <p aria-hidden="true" className="invisible hidden lg:block font-body text-[0.82rem] lg:[@media(max-height:920px)]:text-[0.72rem] leading-relaxed lg:[@media(max-height:920px)]:leading-[1.5] lg:mb-4 lg:[@media(max-height:920px)]:mb-2.5 lg:min-h-[4rem] lg:[@media(max-height:920px)]:min-h-[3.3rem]">&nbsp;</p>
                  <span className={`inline-flex items-center gap-2 font-mono-data text-[0.62rem] lg:[@media(max-height:920px)]:text-[0.56rem] tracking-[0.2em] text-white/70 transition-colors duration-500 ${b.hp}`}>
                    {(b.key === "eyes" ? g.eyesCta : g.commerceCta).toUpperCase()}
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 hp-arrow" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </section>

      <footer className="flex flex-wrap items-end justify-between lg:grid lg:grid-cols-[1fr_auto_1fr] gap-4 px-6 md:px-10 lg:px-16 py-5 lg:[@media(max-height:920px)]:py-3 shrink-0" data-testid="inc-footer">
        <div>
          <p className="font-gateway font-bold tracking-[0.24em] text-[0.72rem]">{g.footerLegal.toUpperCase()}</p>
          <p className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 mt-1.5">{g.tagline}</p>
        </div>
        <LegalLinks className="font-mono-data text-[0.58rem] tracking-[0.18em] text-white/40 order-last w-full text-center lg:order-none lg:w-auto" />
        <span className="lg:justify-self-end font-mono-data text-[0.6rem] tracking-[0.22em] text-white/40">© {new Date().getFullYear()} {g.footerBrand}</span>
      </footer>
    </main>
  );
}
